import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { IS_DEMO_MODE } from '@/config/config'
import { FileText, Search, Filter, RotateCcw, SortAsc, SortDesc, ClipboardList, ChevronDown, X, Pencil, Check, Trash2, ChevronDown as ChevronDownSmall } from 'lucide-react'
import { RichNoteEditor } from '@/components/ui/RichNoteEditor'
import { PageHeader } from '@/components/ui/PageHeader'
import { useAppData } from '@/contexts/AppDataContext'
import { displayBeneficiaryName } from '@/utils/beneficiaryDisplay'
import { useAuth } from '@/contexts/AuthContext'

const NOTE_TYPES = ['entretien', "réunion d'équipe", 'anamnèse', 'contacts réseau']
const NOTES_PER_PAGE = 50

const TYPE_COLORS: Record<string, string> = {
    'entretien': 'bg-blue-100 text-blue-700',
    "réunion d'équipe": 'bg-purple-100 text-purple-700',
    'anamnèse': 'bg-emerald-100 text-emerald-700',
    'contacts réseau': 'bg-orange-100 text-orange-700',
}

interface Note {
    id: string
    beneficiary_id: string
    note_date: string
    note: string
    comment: string | null
    worker_name: string
    colleague_names: string[]
    colleague_ids: string[]
    type: string | null
    created_at: string | null
    updated_at: string | null
}

interface Worker {
    id: string
    name: string
}

interface NotesConsultationProps {
    onNavigateHome: () => void
    onNavigateToTenantNotes: (tenantId: string, tenantName: string) => void
    onNavigateToBeneficiaire?: (id: string) => void
    embedded?: boolean
}

export const NotesConsultation = ({
    onNavigateHome,
    onNavigateToTenantNotes,
    onNavigateToBeneficiaire,
    embedded = false,
}: NotesConsultationProps) => {
    const { summaries } = useAppData()
    const { userProfile } = useAuth()
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const [showInactive, setShowInactive] = useState(false)
    const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([])
    const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false)
    const [tenantSearch, setTenantSearch] = useState('')

    const [keyword, setKeyword] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
    const [selectedWorkerIds, setSelectedWorkerIds] = useState<string[]>([])
    const [selectedTypes, setSelectedTypes] = useState<string[]>([])

    const [notes, setNotes] = useState<Note[]>([])
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [workers, setWorkers] = useState<Worker[]>([])
    interface EditState { noteId: string; text: string; type: string; date: string; colleagueIds: string[]; _k: number }
    const [editState, setEditState] = useState<EditState | null>(null)
    const [autoSaving, setAutoSaving] = useState(false)
    const [hasUnsaved, setHasUnsaved] = useState(false)
    const [justSaved, setJustSaved] = useState(false)
    const editSnapshotRef = useRef<EditState | null>(null)
    const editValuesRef = useRef<EditState>({ noteId: '', text: '', type: '', date: '', colleagueIds: [], _k: 0 })
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const editKeyCounterRef = useRef(0)
    const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null)
    const [deletingConfirmId, setDeletingConfirmId] = useState<string | null>(null)

    // Tenant options for multi-select
    const tenantOptions = summaries
        .filter(s => (s.prenom || s.nom || s.name) && (showInactive || s.is_active))
        .map(s => ({
            id: s.id,
            name: displayBeneficiaryName(s),
            noteCount: (s as any).note_count ?? 0,
            isActive: s.is_active,
            numero_suivi: s.numero_suivi,
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'))

    const filteredTenantOptions = tenantOptions.filter(t =>
        !tenantSearch || t.name.toLowerCase().includes(tenantSearch.toLowerCase())
        || String(t.numero_suivi || '').includes(tenantSearch)
    )

    // Tenants whose notes to show (empty selection = all)
    const effectiveTenantIds = selectedTenantIds.length > 0
        ? selectedTenantIds
        : tenantOptions.map(t => t.id)

    const tenantNameById = Object.fromEntries(
        summaries.map(s => [s.id, displayBeneficiaryName(s)])
    )

    const processNotesData = async (notesData: any[]): Promise<Note[]> => {
        const uniqueWorkerIds = new Set<string>()
        for (const note of notesData) {
            if (note.worker_id) uniqueWorkerIds.add(note.worker_id)
            if (note.colleague_ids) note.colleague_ids.forEach((id: string) => uniqueWorkerIds.add(id))
        }

        const workerCache = new Map<string, string>()
        if (uniqueWorkerIds.size > 0) {
            const { data: workersData } = await supabase
                .from('workers').select('id, name').in('id', Array.from(uniqueWorkerIds))
            workersData?.forEach(w => { if (w.name) workerCache.set(w.id, w.name) })
        }

        return notesData.map(note => ({
            id: note.id,
            beneficiary_id: note.beneficiary_id,
            note_date: note.note_date,
            note: note.note,
            comment: note.comment,
            worker_name: note.worker_id ? (workerCache.get(note.worker_id) || 'Inconnu') : 'Inconnu',
            colleague_ids: note.colleague_ids ?? [],
            colleague_names: note.colleague_ids
                ? note.colleague_ids.map((id: string) => workerCache.get(id)).filter((n: any): n is string => !!n)
                : [],
            type: note.type || null,
            created_at: note.created_at || null,
            updated_at: note.updated_at || null,
        }))
    }

    const fetchNotes = async (offset = 0) => {
        if (effectiveTenantIds.length === 0) {
            if (offset === 0) setNotes([])
            return
        }
        if (offset === 0) setLoading(true)

        let query = supabase.from('notes').select('*')
            .in('beneficiary_id', effectiveTenantIds)
            .is('deleted_dt', null)
            .order('note_date', { ascending: sortOrder === 'asc' })
            .range(offset, offset + NOTES_PER_PAGE - 1)

        const kw = keyword.trim().replace(/\u0027/g, "\u2019")
        if (kw) query = query.or(`note.ilike.%${kw}%,comment.ilike.%${kw}%`)
        if (startDate) query = query.gte('note_date', startDate)
        if (endDate) query = query.lte('note_date', endDate)
        if (selectedTypes.length > 0) {
            const normalized = selectedTypes.map(t => t.replace(/\u0027/g, "\u2019"))
            query = query.in('type', normalized)
        }

        if (selectedWorkerIds.length > 0) {
            query = query.or(`worker_id.in.(${selectedWorkerIds.join(',')}),colleague_ids.ov.{${selectedWorkerIds.join(',')}}`)
        }

        const { data } = await query
        const processed = await processNotesData(data || [])
        if (offset === 0) setNotes(processed)
        else setNotes(prev => [...prev, ...processed])
        setHasMore((data?.length ?? 0) === NOTES_PER_PAGE)
        setLoading(false)
    }

    // Load workers on mount
    useEffect(() => {
        supabase.from('workers').select('id, name').order('name').then(({ data }) => {
            if (data) setWorkers(data.filter(w => w.name !== null).map(w => ({ id: w.id, name: w.name! })))
        })
    }, [])


    // Fetch notes when filters change (debounced for keyword)
    useEffect(() => {
        if (!userProfile || summaries.length === 0) return
        if (debounceRef.current) clearTimeout(debounceRef.current)
        const delay = keyword.trim() ? 400 : 0
        debounceRef.current = setTimeout(() => { fetchNotes(0) }, delay)
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    }, [keyword, startDate, endDate, sortOrder, selectedWorkerIds, selectedTypes, selectedTenantIds, showInactive, userProfile, summaries.length])

    const clearFilters = () => {
        setKeyword('')
        setStartDate('')
        setEndDate('')
        setSelectedWorkerIds([])
        setSelectedTypes([])
        setSelectedTenantIds([])
        setSortOrder('desc')
    }

    const toggleTenant = (id: string) => {
        setSelectedTenantIds(prev =>
            prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
        )
    }

    const startEditNote = (note: Note) => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
        editKeyCounterRef.current += 1
        const snap: EditState = { noteId: note.id, text: note.note, type: (note.type ?? '').replace(/\u2019/g, "\u0027"), date: note.note_date, colleagueIds: [...note.colleague_ids], _k: editKeyCounterRef.current }
        editSnapshotRef.current = snap
        editValuesRef.current = { ...snap }
        setEditState(snap)
        setHasUnsaved(false)
        setJustSaved(false)
    }

    const doSave = async () => {
        const { text, type, date, colleagueIds, noteId } = editValuesRef.current
        if (!noteId) return
        const snap = editSnapshotRef.current
        if (snap) {
            const unchanged = text === snap.text && type === snap.type && date === snap.date
                && JSON.stringify([...colleagueIds].sort()) === JSON.stringify([...snap.colleagueIds].sort())
            if (unchanged) { setHasUnsaved(false); return }
        }
        setAutoSaving(true)
        const now = new Date().toISOString()
        const { error } = await supabase.from('notes').update({
            note: text, type: type ? type.replace(/\u0027/g, "\u2019") : null, note_date: date, colleague_ids: colleagueIds, updated_at: now,
        }).eq('id', noteId)
        if (!error) {
            const newColleagueNames = workers.filter(w => colleagueIds.includes(w.id)).map(w => w.name!).filter(Boolean)
            setNotes(prev => prev.map(n => n.id === noteId
                ? { ...n, note: text, type: type ? type.replace(/\u0027/g, "\u2019") : null, note_date: date, colleague_ids: colleagueIds, colleague_names: newColleagueNames, updated_at: now }
                : n
            ))
            setHasUnsaved(false)
            setJustSaved(true)
            setTimeout(() => setJustSaved(false), 2000)
        }
        setAutoSaving(false)
    }

    const updateEdit = (patch: Partial<EditState>) => {
        setEditState(prev => prev ? { ...prev, ...patch } : null)
        Object.assign(editValuesRef.current, patch)
        setHasUnsaved(true)
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
        autoSaveTimerRef.current = setTimeout(doSave, 1500)
    }

    const saveAndClose = async () => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
        await doSave()
        setEditState(null)
        setHasUnsaved(false)
        setJustSaved(false)
    }

    const closeEdit = () => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
        setEditState(null)
        setHasUnsaved(false)
        setJustSaved(false)
    }

    const softDeleteNote = async (noteId: string) => {
        setDeletingNoteId(noteId)
        const { error } = await supabase.from('notes').update({ deleted_dt: new Date().toISOString() }).eq('id', noteId)
        if (!error) setNotes(prev => prev.filter(n => n.id !== noteId))
        setDeletingNoteId(null)
        setDeletingConfirmId(null)
    }

    const formatDate = (dateString: string) =>
        new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })

    const formatDateTime = (isoString: string | null) => {
        if (!isoString) return null
        const d = new Date(isoString)
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
            + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    }

    const selectedTenantLabels = selectedTenantIds.length === 0
        ? 'Tous les locataires'
        : selectedTenantIds.length <= 2
            ? selectedTenantIds.map(id => tenantNameById[id] || id).join(', ')
            : `${selectedTenantIds.length} locataires sélectionné·e·s`

    return (
        <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
            {!embedded && <PageHeader title="Consultation de notes" icon={<FileText size={20} />} onBack={onNavigateHome} />}

            <main className="max-w-4xl mx-auto py-6 px-4">
                {/* Filter Panel */}
                <div className="bg-white rounded-lg shadow mb-6 p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                            <Filter size={18} />
                            Filtres
                        </h2>
                        <Button variant="outline" size="sm" onClick={clearFilters} className="flex items-center gap-1.5">
                            <RotateCcw size={14} />
                            <span className="hidden sm:inline">Réinitialiser</span>
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Tenant multi-select */}
                        <div className="relative md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Locataires
                                <label className="ml-3 font-normal text-gray-500 cursor-pointer inline-flex items-center gap-1">
                                    <input
                                        type="checkbox"
                                        checked={showInactive}
                                        onChange={e => setShowInactive(e.target.checked)}
                                        className="rounded"
                                    />
                                    <span className="text-xs">Afficher inactifs</span>
                                </label>
                            </label>
                            <button
                                type="button"
                                onClick={() => setTenantDropdownOpen(o => !o)}
                                className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <span className="truncate">{selectedTenantLabels}</span>
                                <div className="flex items-center gap-1 shrink-0">
                                    {selectedTenantIds.length > 0 && (
                                        <span
                                            role="button"
                                            onClick={e => { e.stopPropagation(); setSelectedTenantIds([]) }}
                                            className="text-gray-400 hover:text-gray-600 cursor-pointer"
                                        >
                                            <X size={14} />
                                        </span>
                                    )}
                                    <ChevronDown size={16} className={`text-gray-400 transition-transform ${tenantDropdownOpen ? 'rotate-180' : ''}`} />
                                </div>
                            </button>
                            {tenantDropdownOpen && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                    <div className="sticky top-0 bg-white border-b border-gray-100 p-2">
                                        <div className="relative">
                                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                value={tenantSearch}
                                                onChange={e => setTenantSearch(e.target.value)}
                                                placeholder="Rechercher..."
                                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                autoFocus
                                            />
                                        </div>
                                    </div>
                                    {filteredTenantOptions.map(t => (
                                        <label
                                            key={t.id}
                                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedTenantIds.includes(t.id)}
                                                onChange={() => toggleTenant(t.id)}
                                                className="rounded"
                                            />
                                            <span className={`text-sm flex-1 ${!t.isActive ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                                                {t.name}
                                                {!t.isActive ? ' (inactif)' : ''}
                                            </span>
                                            <span className="text-xs text-gray-400">({t.noteCount})</span>
                                        </label>
                                    ))}
                                    {filteredTenantOptions.length === 0 && (
                                        <p className="text-sm text-gray-400 text-center py-4">Aucun résultat</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Keyword */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Mots-clés</label>
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={keyword}
                                    onChange={e => setKeyword(e.target.value)}
                                    placeholder="Rechercher dans les notes..."
                                    className="pl-9 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>
                        </div>

                        {/* Sort */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tri</label>
                            <button
                                type="button"
                                onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
                                className="w-full flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 hover:border-gray-400"
                            >
                                {sortOrder === 'desc' ? <SortDesc size={16} /> : <SortAsc size={16} />}
                                {sortOrder === 'desc' ? 'Plus récentes en premier' : 'Plus anciennes en premier'}
                            </button>
                        </div>

                        {/* Date range */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                        </div>

                        {/* Worker filter */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Travailleurs</label>
                            <div className="flex flex-wrap gap-2">
                                {workers.map(w => (
                                    <label key={w.id} className="flex items-center gap-1.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedWorkerIds.includes(w.id)}
                                            onChange={e => {
                                                if (e.target.checked) setSelectedWorkerIds(prev => [...prev, w.id])
                                                else setSelectedWorkerIds(prev => prev.filter(id => id !== w.id))
                                            }}
                                            className="rounded"
                                        />
                                        <span className="text-sm text-gray-700">{w.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Type filter */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type de note</label>
                            <div className="flex flex-wrap gap-2">
                                {NOTE_TYPES.map(type => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => {
                                            setSelectedTypes(prev =>
                                                prev.includes(type) ? [] : [type]
                                            )
                                        }}
                                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                                            selectedTypes.includes(type)
                                                ? (TYPE_COLORS[type] || 'bg-blue-100 text-blue-700') + ' border-transparent'
                                                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                                        }`}
                                    >
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Notes list */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                    </div>
                ) : notes.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <FileText className="mx-auto mb-4" size={40} />
                        <p>Aucune note trouvée</p>
                    </div>
                ) : (
                    <>
                        <p className="text-xs text-gray-400 mb-3 pl-1">{notes.length} note{notes.length > 1 ? 's' : ''} affichée{notes.length > 1 ? 's' : ''}</p>
                        <div className="space-y-3">
                            {notes.map(note => {
                                const tenantName = tenantNameById[note.beneficiary_id] || '—'
                                const es = editState?.noteId === note.id ? editState : null
                                return (
                                        <div key={note.id} className={`bg-white rounded-lg shadow-sm border p-4 ${es ? 'border-blue-300' : 'border-gray-200'}`}>

                                            {/* ── Header ── */}
                                            <div className="flex items-start justify-between gap-2 mb-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <button type="button" onClick={() => onNavigateToTenantNotes(note.beneficiary_id, tenantName)}
                                                        className="text-sm font-semibold text-blue-700 hover:underline">{tenantName}</button>
                                                    {onNavigateToBeneficiaire && (
                                                        <button type="button" onClick={() => onNavigateToBeneficiaire(note.beneficiary_id)}
                                                            title="Voir la fiche" className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors">
                                                            <ClipboardList size={14} />
                                                        </button>
                                                    )}
                                                    {es ? (
                                                        <select key={`type-${es._k}`} defaultValue={es.type}
                                                            onChange={e => updateEdit({ type: e.target.value })}
                                                            className="text-xs border border-blue-300 rounded-full px-2 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500">
                                                            <option value="">— type —</option>
                                                            {NOTE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                                                        </select>
                                                    ) : note.type ? (
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[note.type.replace(/’/g, "'")] || 'bg-gray-100 text-gray-600'}`}>
                                                            {note.type.charAt(0).toUpperCase() + note.type.slice(1)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    {es ? (
                                                        <input key={`date-${es._k}`} type="date" defaultValue={es.date}
                                                            onChange={e => updateEdit({ date: e.target.value })}
                                                            className="text-xs border border-blue-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                                    ) : (
                                                        <span className="text-sm text-gray-500 whitespace-nowrap">{formatDate(note.note_date)}</span>
                                                    )}
                                                    {!IS_DEMO_MODE && (<>
                                                    <button type="button" onClick={() => es ? closeEdit() : startEditNote(note)}
                                                        className={`p-1 rounded transition-colors ${es ? 'text-blue-500 bg-blue-50' : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'}`}
                                                        title={es ? 'Fermer' : 'Modifier'}>
                                                        <Pencil size={13} />
                                                    </button>
                                                    {deletingConfirmId === note.id ? (
                                                        <div className="flex items-center gap-1 text-xs">
                                                            <span className="text-red-600 font-medium">Supprimer ?</span>
                                                            <button type="button" onClick={() => softDeleteNote(note.id)} disabled={deletingNoteId === note.id}
                                                                className="px-1.5 py-0.5 rounded bg-red-500 text-white hover:bg-red-600 disabled:opacity-50">Oui</button>
                                                            <button type="button" onClick={() => setDeletingConfirmId(null)}
                                                                className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200">Non</button>
                                                        </div>
                                                    ) : (
                                                        <button type="button" onClick={() => setDeletingConfirmId(note.id)}
                                                            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Supprimer">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                    </>)}
                                                </div>
                                            </div>

                                            {/* ── Action bar ── */}
                                            {es && (
                                                <div className="flex items-center gap-2 mb-2">
                                                    {autoSaving && <span className="text-xs text-gray-400 flex items-center gap-1"><span className="inline-block w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> Sauvegarde...</span>}
                                                    {!autoSaving && justSaved && <span className="text-xs text-green-600 flex items-center gap-1"><Check size={11} /> Sauvegardé</span>}
                                                    <button type="button" onClick={hasUnsaved ? saveAndClose : closeEdit} disabled={autoSaving}
                                                        className={`text-xs px-3 py-1 rounded border transition-colors disabled:opacity-50 ${hasUnsaved ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                                                        {hasUnsaved ? 'Sauver et fermer' : 'Fermer'}
                                                    </button>
                                                </div>
                                            )}

                                            {/* ── Content ── */}
                                            {es ? (
                                                <RichNoteEditor value={es.text} onChange={html => updateEdit({ text: html })} minHeight={120} autoFocus />
                                            ) : (
                                                <div className="text-sm text-gray-800 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: note.note }} />
                                            )}
                                            {note.comment && <p className="mt-2 text-sm text-gray-500 italic">{note.comment}</p>}

                                            {/* ── Footer ── */}
                                            <div className="mt-3 flex flex-wrap items-start justify-between gap-1">
                                                <div className="text-xs text-gray-400 space-y-0.5">
                                                    <div><span className="font-medium text-gray-500">Rédigé par :</span> {note.worker_name}</div>
                                                    {es ? (
                                                        <div className="flex flex-wrap gap-2 items-center">
                                                            <span className="font-medium text-gray-500">Présent.e.s :</span>
                                                            {workers.filter(w => w.name !== note.worker_name).map(w => (
                                                                <label key={w.id} className="flex items-center gap-1 cursor-pointer">
                                                                    <input type="checkbox" checked={es.colleagueIds.includes(w.id)}
                                                                        onChange={e => updateEdit({ colleagueIds: e.target.checked ? [...es.colleagueIds, w.id] : es.colleagueIds.filter(id => id !== w.id) })}
                                                                        className="rounded" />
                                                                    <span>{w.name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    ) : note.colleague_names.length > 0 ? (
                                                        <div><span className="font-medium text-gray-500">Présent.e.s :</span> {note.colleague_names.join(', ')}</div>
                                                    ) : null}
                                                </div>
                                                <span className="text-xs text-gray-300 italic text-right">
                                                    Créé {formatDateTime(note.created_at)}
                                                    {note.updated_at && note.updated_at !== note.created_at && <><br />Modifié {formatDateTime(note.updated_at)}</>}
                                                </span>
                                            </div>
                                        </div>
                                )
                            })}
                        </div>
                        {hasMore && (
                            <div className="flex justify-center mt-6">
                                <Button variant="outline" onClick={() => fetchNotes(notes.length)}>
                                    Charger plus
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Close dropdown on outside click */}
            {tenantDropdownOpen && (
                <div className="fixed inset-0 z-10" onClick={() => setTenantDropdownOpen(false)} />
            )}
        </div>
    )
}
